// `/debug/card` — renders 10 randomized IDCards in a grid for visual QA, plus
// the 3 composited share assets (isolated card + 3×4 + 9×16) for the first
// card in the grid so the full pipeline is visible end-to-end.
import { useEffect, useMemo, useRef, useState } from 'react';
import IDCard, { type IDCardOverride } from '../components/IDCard';
import i18n, { SUPPORTED_LANGS, type Lang } from '../i18n';
import { composeCard, composeShare } from '../lib/composeShare';
import { rasterizeLockup } from '../lib/photo';
import type { FormData } from '../state/flow';
import './DebugCard.scss';

const CARD_COUNT = 9;

// Mixed-length, multi-cultural names — long ones stress the auto-shrink path,
// short ones verify the layout doesn't collapse.
const NAMES: ReadonlyArray<readonly [string, string]> = [
  ['Lin', 'Wu'],
  ['Ana', 'Silva'],
  ['Tom', 'Lee'],
  ['Bo', 'Kim'],
  ['Marie', 'Dubois'],
  ['Hiroshi', 'Tanaka'],
  ['Kwame', 'Mensah'],
  ['Priya', 'Shah'],
  ['Carlos', 'Rodríguez'],
  ['Yuki', 'Saito'],
  ['Sofia', 'Müller'],
  ['Henrik', 'Lindqvist'],
  ['Liam', "O'Brien"],
  ['Zaynab', 'Al-Hassan'],
  ['Olusegun', 'Adekunle'],
  ['Theodora', 'Constantinou'],
  ['Aleksandra', 'Vasilevska'],
  ['Maximilian', 'Aldenburg-Hofstätter'],
  ['Christopher', 'Pemberton-Stewart'],
  ['Anne-Sophie', 'Beauchamp-Laurier'],
  ['Bartholomew', 'Witherspoon'],
  ['Jean-Christophe', 'de la Roquefort'],
];

// A handful of tinted silhouette SVGs so the grid isn't visually monotonous.
const PHOTO_PALETTE: ReadonlyArray<readonly [string, string]> = [
  ['#7b8a99', '#4a5566'],
  ['#a98a72', '#6b5340'],
  ['#c89b7c', '#8a6448'],
  ['#8a7a6a', '#594a3c'],
  ['#9aa3ad', '#5a6470'],
  ['#b8a07c', '#7c6444'],
];

function silhouette(bg: string, fg: string): string {
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 5 6'>` +
    `<rect width='5' height='6' fill='${bg}'/>` +
    `<circle cx='2.5' cy='2.3' r='1.1' fill='${fg}'/>` +
    `<path d='M0.5 5.5 Q 2.5 3.4 4.5 5.5 L 4.5 6 L 0.5 6 Z' fill='${fg}'/>` +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function pick<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

// 1–4 distinct clubs to exercise the multi-select rendering on the card.
function pickClubs(items: readonly string[]): string[] {
  const count = 1 + Math.floor(Math.random() * Math.min(4, items.length));
  const pool = [...items];
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.floor(Math.random() * pool.length);
    out.push(pool.splice(idx, 1)[0]);
  }
  return out;
}

function randomCard(): IDCardOverride & { lang: Lang } {
  const lang = pick(SUPPORTED_LANGS);
  const t = i18n.getFixedT(lang);
  const majors = t('majors', { returnObjects: true }) as string[];
  const minors = t('minors', { returnObjects: true }) as string[];
  const clubs = t('clubs', { returnObjects: true }) as string[];
  const [firstName, lastName] = pick(NAMES);
  const [bg, fg] = pick(PHOTO_PALETTE);

  // PT's minors list aliases majors (Dupla Graduação reuses the same
  // curriculum), so pick distinct values to avoid Major == Minor on the card.
  const major = pick(majors);
  const minorPool = minors.filter((m) => m !== major);
  const form: FormData = {
    firstName,
    lastName,
    major,
    minor: minorPool.length > 0 ? pick(minorPool) : major,
    clubs: pickClubs(clubs),
  };

  return { lang, locale: lang, form, photoDataUrl: silhouette(bg, fg) };
}

interface Composites {
  card: string | null;
  '3x4': string | null;
  '9x16': string | null;
}

export default function DebugCard() {
  const cards = useMemo(() => Array.from({ length: CARD_COUNT }, randomCard), []);
  const firstCard = cards[0];

  const lockupRef = useRef<HTMLDivElement | null>(null);
  const [composites, setComposites] = useState<Composites>({
    card: null,
    '3x4': null,
    '9x16': null,
  });

  useEffect(() => {
    let alive = true;
    void (async () => {
      if (!lockupRef.current) return;
      try {
        const lockup = await rasterizeLockup(lockupRef.current);
        if (!alive) return;
        const [card, square, tall] = await Promise.all([
          composeCard(lockup),
          composeShare('3x4', lockup, firstCard.locale),
          composeShare('9x16', lockup, firstCard.locale),
        ]);
        if (!alive) return;
        setComposites({ card, '3x4': square, '9x16': tall });
      } catch {
        // Leave composites null; skeletons will keep showing.
      }
    })();
    return () => {
      alive = false;
    };
  }, [firstCard]);

  return (
    <div className="debug-card">
      <header className="debug-card__head">
        <h1>IDCard QA grid</h1>
        <p>
          {CARD_COUNT} randomized cards across {SUPPORTED_LANGS.join(' / ')}. Reload to reroll.
        </p>
      </header>

      <div className="debug-card__grid">
        {cards.map((card, i) => (
          <figure key={i} className="debug-card__cell">
            <div className="debug-card__scaler">
              <img src="/img/card.png" alt="" className="debug-card__bg" />
              <IDCard override={card} />
            </div>
            <figcaption className="debug-card__caption">
              {card.lang.toUpperCase()} · {card.form.firstName} {card.form.lastName}
            </figcaption>
          </figure>
        ))}
      </div>

      <section className="debug-card__shareables">
        <h2>
          Composited share assets — first card ({firstCard.lang.toUpperCase()} ·{' '}
          {firstCard.form.firstName} {firstCard.form.lastName})
        </h2>
        <div className="debug-card__shareable-row">
          <Composite label="card.png alpha" src={composites.card} aspect="695 / 1037" />
          <Composite label="3 × 4 share" src={composites['3x4']} aspect="1080 / 1447" />
          <Composite label="9 × 16 share" src={composites['9x16']} aspect="1080 / 1920" />
        </div>
      </section>

      <div className="debug-card__offscreen" aria-hidden="true">
        <IDCard ref={lockupRef} override={firstCard} />
      </div>
    </div>
  );
}

function Composite({
  label,
  src,
  aspect,
}: {
  label: string;
  src: string | null;
  aspect: string;
}) {
  return (
    <figure className="debug-card__composite">
      <div className="debug-card__composite-frame" style={{ aspectRatio: aspect }}>
        {src ? <img src={src} alt="" /> : <div className="debug-card__composite-skeleton" />}
      </div>
      <figcaption className="debug-card__composite-label">{label}</figcaption>
    </figure>
  );
}
